import { useReducer, useMemo } from 'react';

import useApi from '../useApi';
import { useAuthContext } from '../../Auth/context';
import { formatDate } from '../../lib/timeFormat';
import reducer from './reducer';
import useInitFetch from './useInitFetch';
import ACTIONS_TYPES from './actionTypes';

const STEPS = [
  {
    id: 1,
    title: 'البحث',
    done: true,
    key: 'search'
  },
  {
    id: 2,
    title: 'الحجز وبيانات المسافرين',
    done: false,
    key: 'passenger'
  },
  {
    id: 3,
    title: 'تأكيد الحجز',
    done: false,
    key: 'payment'
  },
  // {
  //   id: 4,
  //   title: 'المقاعد',
  //   done: false,
  //   key: 'seats'
  // },
];

const INITIAL_STATE = {
  activeStep: 'passenger',
  passengers: [],
  booking: {},
  steps: STEPS,
  payment: {},
  errors: {},
  expired: false,
  status: 'initial',
  seat: {},
};

const STAT = { count: 0, totalAmount: 0 };

const useBookingState = ({ bookingId, redirectToOffers }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const {
    actions: { getBooking, updateBooking, payBooking },
    data: { countries }
  } = useApi();
  const { state: { user } } = useAuthContext();

  const currentStep = useMemo(
    () => state.steps.find(({ key }) => key === state.activeStep),
    [state.activeStep, state.steps],
  );

  const calculateTotals = (list) => list.reduce((acc, { price, isSelected }) => ({
    ...acc,
    price: isSelected ? acc.price + Number(price) : acc.price,
    count: isSelected ? acc.count + 1 : acc.count
  }), { price: 0, weight: 0, count: 0 });

  const passengersStats = useMemo(
    () => state.passengers.reduce((acc, { passengerType, amount, baggage, extraBaggage }) => {
      const { bookingAmount, holdBaggageTotal, carryOnBaggageTotal } = acc;

      const { price: holdTotalAmount, count: holdCount } = calculateTotals(extraBaggage.hold);
      const { price: carryOnTotalAmount, count: carryOnCount } = calculateTotals(extraBaggage.carryOn);

      const holdUpdatedAmount = holdBaggageTotal.totalAmount + holdTotalAmount;
      const carryOnUpdatedAmount = carryOnBaggageTotal.totalAmount + carryOnTotalAmount;

      return {
        ...acc,
        [passengerType]: {
          totalAmount: acc[passengerType].totalAmount + amount,
          count: acc[passengerType].count + 1,
        },
        holdBaggageTotal: {
          totalAmount: holdUpdatedAmount,
          count: holdBaggageTotal.count + holdCount
        },
        carryOnBaggageTotal: {
          totalAmount: carryOnUpdatedAmount,
          count: carryOnBaggageTotal.count + carryOnCount
        },
        bookingAmount: bookingAmount + holdUpdatedAmount + carryOnUpdatedAmount
      };
    }, {
      adult: STAT,
      child: STAT,
      infant: STAT, holdBaggageTotal: STAT,
      carryOnBaggageTotal: STAT,
      bookingAmount: Number(state.booking.amount)
    }),
    [state.passengers, state.booking.amount],
  );

  useInitFetch({ state, dispatch, bookingId, getBooking });

  const actions = ({
    setLoading() {
      dispatch({ type: ACTIONS_TYPES.UPDATE_STATUS, status: 'loading' });
    },
    setSubmitting() {
      dispatch({ type: ACTIONS_TYPES.UPDATE_STATUS, status: 'submitting' });
    },
    toggleStep(step) {
      if (step.key !== 'search' && step.done) {
        dispatch({ type: ACTIONS_TYPES.TOGGLE_STEP, step: step.key });
      }
    },
    next() {
      const nextStep = state.steps.find(({ id }) => id === currentStep.id + 1);
      dispatch({ type: ACTIONS_TYPES.TOGGLE_STEP, step: nextStep.key });
    },
    back() {
      const previousStep = state.steps.find(({ id }) => id === currentStep.id - 1);
      dispatch({ type: ACTIONS_TYPES.TOGGLE_STEP, step: previousStep.key });
    },
    handleBookingChange(update) {
      dispatch({ type: ACTIONS_TYPES.BOOKING_CHANGE, update });
    },
    handlePassengerChange(passengerId, update) {
      dispatch({ type: ACTIONS_TYPES.PASSENGER_UPDATE, passengerId, update });
    },
    handleSeatChange(seatNumber, update) {
      dispatch({ type: ACTIONS_TYPES.SEAT_UPDATE, seatNumber, update });
    },
    submit: async () => {
      const nextStep = state.steps.find(({ id }) => id === currentStep.id + 1);
      const updatedPassengers = state.passengers.map(({
        issuingCountry,
        extraBaggage,
        freeBaggage,
        dateOfBirth,
        documentExpiryDate,
        documentIssuanceDate,
        firstName,
        lastName,
        ...passenger
      }) => ({
        ...passenger,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth: formatDate(dateOfBirth),
        documentExpiryDate: formatDate(documentExpiryDate),
        documentIssuanceDate: formatDate(documentIssuanceDate)
      }));

      actions.setSubmitting();

      try {
        const { data } = await updateBooking({
          bookingId: state.booking.id,
          passengers: updatedPassengers,
          email: state.booking.email,
          mobile: state.booking.mobile,
        });

        if (data.success) {
          dispatch({ type: ACTIONS_TYPES.SUBMIT_BOOKING, step: nextStep.key });
          if (data.booking?.pnr) dispatch({ type: ACTIONS_TYPES.BOOKING_CHANGE, update: { pnr: data.booking.pnr } });
        } else {
          if (['OFFER_CORRUPTED', 'NoOfferFound', 'DupePassengerFlight', 'Cannot read properties of undefined (reading \'data\')'].includes(data.message)) {
            dispatch({ type: ACTIONS_TYPES.SET_EXPIRED });
          }
          else dispatch({
            type: ACTIONS_TYPES.ALERT_ERROR,
            error: 'يرجى مراجعة بيانات المسافرين',
            errors: data.validationErrors
          });
        }
      } catch (error) {
        dispatch({ type: ACTIONS_TYPES.ALERT_ERROR, error: error.networkError ? 'الرجاء التحقق من إتصالك بالإنترنت' : 'نأسف, حدث خطأ غير متوقع, حاول مرة أخرى بعد قليل' });
      }
    },

    finalize: ({ payLater }) => {
      actions.setSubmitting();

      payBooking(state.booking.id, payLater).then(({ data }) => {
        if (data.success) {
          if (payLater) {
            dispatch({ type: ACTIONS_TYPES.TOGGLE_STEP, step: 'final' });
          } else window.location.href = data.booking.paymentLink;
        } else {
          if (['OFFER_CORRUPTED', 'NoOfferFound'].includes(data.message)) {
            dispatch({ type: ACTIONS_TYPES.SET_EXPIRED });
          }
          else dispatch({ type: ACTIONS_TYPES.ALERT_ERROR, error: 'نأسف, حدث خطأ غير متوقع, حاول مرة أخرى بعد قليل' });
        }
      }).catch((error) => dispatch({ type: ACTIONS_TYPES.ALERT_ERROR, error: error.networkError ? 'الرجاء التحقق من إتصالك بالإنترنت' : 'نأسف, حدث خطأ غير متوقع, حاول مرة أخرى بعد قليل' }));
    },

    goToSearch: () => {
      const { booking: { slices }, booking } = state;

      const originSlice = slices[0];
      const destinationSliceIndex = booking.tripType === 'round-trip'
        ? slices.length - 2
        : slices.length - 1;

      const destinationSlice = slices[destinationSliceIndex] || {};

      const params = {
        tripType: booking.tripType,
        cabinClass: booking.cabinClass,
        departureStation: originSlice.origin,
        destinationStation: booking.tripType === 'one-way' ? destinationSlice.destination : originSlice.destination,
        departureDate: new Date(originSlice.departureDate),
        returnDate: booking.tripType === 'round-trip' && new Date(destinationSlice.arrivalDate),
        adults: passengersStats.adult.count,
        children: passengersStats.child.count,
        infants: passengersStats.infant.count,
        hold: booking.requestedHoldBaggage,
        carryOn: booking.requestedCarryOnBaggage,
        slices: booking.tripType === 'multi-city'
          ? slices.map(slice => ({
            departureStation: slice.origin,
            destinationStation: slice.destination,
            departureDate: slice.departureDate
          }))
          : [],
      };
      return redirectToOffers(params);
    },
    toggleBagSelect: (item, passenger) => dispatch({
      type: ACTIONS_TYPES.PASSENGER_BAGGAGE_UPDATE,
      item: { ...item, isSelected: !item.isSelected },
      passenger
    }),
  });

  const meta = {
    countries,
  };

  return { state, actions, meta, stats: { passengersStats } };
};

export default useBookingState;

